"use client";

import { useId } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Settings, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils/cx";
import SupportUnreadBadge from "@/components/support/SupportUnreadBadge";
import {
  CUSTOMER_NAV_GROUPS,
  CUSTOMER_PRIMARY_NAV_ITEMS,
  CUSTOMER_SUPPORT_NAV_ITEM,
  isCustomerNavItemActive,
} from "@/components/nav/customerNavigation";

function getSupportNavigationLabel(title, count, showSupportBadge) {
  if (!showSupportBadge || count <= 0) return undefined;
  return `${title}, ${count} unread`;
}

function getLegalOnboardingHref(href) {
  if (typeof href !== "string" || !href.startsWith("/")) return href;
  if (
    !href.startsWith("/customer/") &&
    !href.startsWith("/account/") &&
    href !== "/customer/home"
  ) {
    return href;
  }
  return `/customer/onboarding?next=${encodeURIComponent(href)}`;
}

function getBusinessShortcutMeta({ businessAccessContexts, businessName }) {
  const contexts = Array.isArray(businessAccessContexts) ? businessAccessContexts : [];
  const primaryContext = contexts[0] || null;
  const isStaffAccess = primaryContext?.accessType === "staff";
  const resolvedBusinessName = String(primaryContext?.businessName || businessName || "").trim();

  return {
    title:
      isStaffAccess && resolvedBusinessName
        ? `${resolvedBusinessName} dashboard`
        : "Business dashboard",
    description: isStaffAccess ? "Manage orders and messages" : "Manage dashboard and settings",
  };
}

export default function AccountMenuItems({
  variant = "dropdown",
  isCustomer = false,
  isBusiness = false,
  hasBusinessAccess = false,
  hasAdminMembership = false,
  businessAccessContexts = [],
  businessName = "",
  unreadCount = 0,
  supportUnreadCount = 0,
  onNavigate = undefined,
  logout = null,
  compact = false,
}) {
  const pathname = usePathname();
  const suppressProtectedPrefetch = pathname === "/customer/onboarding";
  const getHref = (href) =>
    suppressProtectedPrefetch ? getLegalOnboardingHref(href) : href;
  const businessShortcut = getBusinessShortcutMeta({ businessAccessContexts, businessName });
  const navId = useId();

  if (variant === "dropdown") {
    return (
      <>
        {isCustomer ? (
          <div className="px-2 pb-1 pt-2 space-y-1">
            {hasAdminMembership ? (
              <Link href="/admin" prefetch={false} onClick={onNavigate} className="flex w-full items-center gap-3 rounded-2xl border border-violet-300/30 bg-violet-500/10 px-3 py-3 text-left transition hover:bg-violet-500/20" data-safe-nav="1">
                <ShieldCheck className="h-5 w-5 text-violet-200" />
                <span className="text-sm font-semibold">Admin account</span>
              </Link>
            ) : null}
            {CUSTOMER_PRIMARY_NAV_ITEMS.map(({ href, title, description, icon: Icon, showBadge }) => (
              <Link
                key={href}
                href={getHref(href)}
                prefetch={suppressProtectedPrefetch ? false : undefined}
                onClick={onNavigate}
                className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 transition hover:bg-white/10 touch-manipulation text-left"
                data-safe-nav="1"
              >
                <div className="h-11 w-11 rounded-2xl bg-white/10 flex items-center justify-center text-white">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold">{title}</p>
                    {showBadge && unreadCount > 0 ? (
                      <span className="rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-semibold !text-white">
                        {unreadCount}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-xs yb-dropdown-muted">{description}</p>
                </div>
              </Link>
            ))}

            {hasBusinessAccess ? (
              <Link
                href={getHref("/business/dashboard")}
                prefetch={suppressProtectedPrefetch ? false : undefined}
                onClick={onNavigate}
                className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 transition hover:bg-white/10 touch-manipulation text-left"
                data-safe-nav="1"
              >
                <div className="h-11 w-11 rounded-2xl bg-white/10 flex items-center justify-center text-white">
                  <Settings className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{businessShortcut.title}</p>
                  <p className="text-xs yb-dropdown-muted">{businessShortcut.description}</p>
                </div>
              </Link>
            ) : null}
          </div>
        ) : null}

        <div className="mt-2 border-t border-white/10 px-4 pt-3">
          {isCustomer ? (
            <div className="space-y-1">
              {[CUSTOMER_SUPPORT_NAV_ITEM].map(({ href, title, description, icon: Icon, showSupportBadge }) => (
                <Link
                  key={href}
                  href={getHref(href)}
                  prefetch={suppressProtectedPrefetch ? false : undefined}
                  onClick={onNavigate}
                  aria-label={getSupportNavigationLabel(title, supportUnreadCount, showSupportBadge)}
                  className="flex w-full items-center gap-3 rounded-2xl bg-white/5 px-4 py-3 text-left transition hover:bg-white/10 touch-manipulation"
                  data-safe-nav="1"
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className={`flex items-center justify-between gap-2 text-sm font-semibold ${showSupportBadge ? "whitespace-nowrap" : ""}`}>{title}<SupportUnreadBadge count={showSupportBadge ? supportUnreadCount : 0} /></span>
                    <span className="block text-xs yb-dropdown-muted">{description}</span>
                  </span>
                </Link>
              ))}
            </div>
          ) : null}
          {logout ? <div className="mt-3">{logout}</div> : null}
        </div>
      </>
    );
  }

  const renderSidebarLink = ({ href, title, description, icon: Icon, showBadge, showSupportBadge }) => {
    const active = isCustomerNavItemActive(pathname, href);
    return (
      <Link
        key={href}
        href={getHref(href)}
        prefetch={suppressProtectedPrefetch ? false : undefined}
        onClick={onNavigate}
        aria-label={getSupportNavigationLabel(title, supportUnreadCount, showSupportBadge)}
        className={cn(
          "group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all duration-150 hover:bg-gray-50",
          active && "bg-purple-100 hover:bg-purple-100"
        )}
        data-safe-nav="1"
        aria-current={active ? "page" : undefined}
      >
        <span
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border-none bg-gray-50 text-gray-600 transition-colors duration-150 group-hover:text-gray-800",
            active && "text-purple-600 group-hover:text-purple-600"
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-2">
            <span className={`text-[15px] font-semibold leading-5 text-gray-900 ${showSupportBadge ? "whitespace-nowrap" : ""}`}>
              {title}
            </span>
            {showBadge && unreadCount > 0 ? (
              <span className="rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-semibold !text-white">
                {unreadCount}
              </span>
            ) : null}
            {showSupportBadge ? <SupportUnreadBadge count={supportUnreadCount} /> : null}
          </span>
          <span className="mt-0.5 block text-[13px] leading-4 text-gray-500">
            {description}
          </span>
        </span>
      </Link>
    );
  };

  return (
    <div className={cn("flex flex-col", compact ? "gap-0" : "gap-6")}>
      {isCustomer && hasAdminMembership ? (
        <Link href="/admin" prefetch={false} onClick={onNavigate} className="flex w-full items-center gap-3 rounded-2xl border border-violet-200 bg-violet-50 px-3 py-3 text-left text-violet-900 transition hover:bg-violet-100" data-safe-nav="1">
          <ShieldCheck className="h-5 w-5" />
          <span className="text-sm font-semibold">Admin account</span>
        </Link>
      ) : null}
      {hasBusinessAccess ? (
        <Link
          href={getHref("/business/dashboard")}
          prefetch={suppressProtectedPrefetch ? false : undefined}
          onClick={onNavigate}
          className="group flex w-full items-center gap-3 rounded-2xl border border-purple-100 bg-purple-50/80 px-3 py-3.5 text-left transition hover:border-purple-200 hover:bg-purple-100/80"
          data-safe-nav="1"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-purple-700 shadow-sm ring-1 ring-purple-100 transition group-hover:text-purple-800">
            <Settings className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[15px] font-semibold leading-5 text-gray-950">
              {businessShortcut.title}
            </span>
            <span className="mt-0.5 block text-[13px] leading-4 text-gray-600">
              {businessShortcut.description}
            </span>
          </span>
        </Link>
      ) : null}

      {isCustomer ? (
        <div>
          <div className="space-y-7">
            {CUSTOMER_NAV_GROUPS.map((group, groupIndex) => {
              const headingId = `${navId}-${group.label.toLowerCase().replace(/\s+/g, "-")}`;
              return (
                <div
                  key={group.label}
                  role="group"
                  aria-labelledby={headingId}
                  className={cn(
                    groupIndex > 0 && "border-t border-gray-100 pt-7",
                    group.label === "Profile" && "pt-8"
                  )}
                >
                  <p id={headingId} className="mb-2 text-xs uppercase tracking-wide text-gray-400/80">
                    {group.label}
                  </p>
                  <div className="space-y-0.5">{group.items.map(renderSidebarLink)}</div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {isCustomer ? (
        // Secondary actions sit apart from the three customer groups.
        <div
          className={cn(
            "border-t border-[var(--yb-border)] pt-4",
            compact && "mt-4"
          )}
        >
          {renderSidebarLink(CUSTOMER_SUPPORT_NAV_ITEM)}
          {logout ? <div className="mt-3">{logout}</div> : null}
        </div>
      ) : logout ? (
        <div
          className={cn(
            "border-t border-[var(--yb-border)] pt-4",
            compact && "mt-4"
          )}
        >
          {logout}
        </div>
      ) : null}
    </div>
  );
}
